import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, TrendingUp, TrendingDown, Package, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Reports() {
  const [summary, setSummary] = useState({ total_income: 0, total_expense: 0, balance: 0, transaction_count: 0 });
  const [categoryReport, setCategoryReport] = useState([]);
  const [dateReport, setDateReport] = useState([]);
  const [productReport, setProductReport] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    categoryType: 'all',
    categoryId: 'all',
    subcategoryId: 'all',
    groupBy: 'month'
  });

  const toggleCategory = (index) => {
    setExpandedCategories(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const expandAll = () => {
    const allExpanded = {};
    categoryReport.forEach((_, index) => {
      allExpanded[index] = true;
    });
    setExpandedCategories(allExpanded);
  };

  const collapseAll = () => {
    setExpandedCategories({});
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchCategories();
      await fetchReports();
    };
    loadData();
  }, []);

  useEffect(() => {
    fetchReports();
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: filters.startDate || undefined,
        end_date: filters.endDate || undefined,
        category_id: (filters.categoryId && filters.categoryId !== 'all') ? filters.categoryId : undefined
      };

      if (filters.subcategoryId && filters.subcategoryId !== 'all') {
        params.category_id = filters.subcategoryId;
      }

      const categoryTypeParam = (filters.categoryType && filters.categoryType !== 'all') ? filters.categoryType : undefined;

      const [summaryRes, categoryRes, dateRes, productRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/reports/summary`, { params }),
        axios.get(`${API}/reports/by-category`, { params: { ...params, type: categoryTypeParam } }),
        axios.get(`${API}/reports/by-date`, { params: { ...params, group_by: filters.groupBy } }),
        axios.get(`${API}/reports/by-product`, { params }),
        axios.get(`${API}/transactions`, { params: { ...params, type: categoryTypeParam } })
      ]);

      setSummary(summaryRes.data);
      setCategoryReport(categoryRes.data);
      setDateReport(dateRes.data);
      setProductReport(productRes.data);
      setAllTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(20);
    doc.text('EGO Cash Flow - Detailed Report', 14, 22);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString('en-US')}`, 14, 30);

    let yPosition = 40;

    doc.setFontSize(14);
    doc.text('Summary', 14, yPosition);
    yPosition += 10;

    autoTable(doc, {
      startY: yPosition,
      head: [['Metric', 'Value']],
      body: [
        ['Total Income', `$${summary.total_income.toFixed(2)}`],
        ['Total Expenses', `$${summary.total_expense.toFixed(2)}`],
        ['Net Balance', `$${summary.balance.toFixed(2)}`],
        ['Transaction Count', summary.transaction_count.toString()]
      ],
      theme: 'grid',
      styles: { fontSize: 10 },
    });

    yPosition = doc.lastAutoTable.finalY + 15;

    doc.setFontSize(14);
    doc.text('Category Details', 14, yPosition);
    yPosition += 10;

    categoryReport.forEach((categoryItem) => {
      const categoryTransactions = allTransactions.filter(trans => {
        const catName = categories.find(c => c.id === trans.category_id)?.name;
        const subCatName = trans.subcategory_id ? categories.find(c => c.id === trans.subcategory_id)?.name : null;
        return catName === categoryItem.category_name || subCatName === categoryItem.category_name;
      });

      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(12);
      doc.text(`${categoryItem.category_name} - Total: $${categoryItem.total_amount.toFixed(2)}`, 14, yPosition);
      yPosition += 8;

      if (categoryTransactions.length > 0) {
        autoTable(doc, {
          startY: yPosition,
          head: [['Date', 'Category', 'Subcategory', 'Description', 'Type', 'Amount']],
          body: categoryTransactions.map(trans => {
            const catName = categories.find(c => c.id === trans.category_id)?.name || '-';
            const subCatName = trans.subcategory_id ? categories.find(c => c.id === trans.subcategory_id)?.name || '-' : '-';
            return [
              new Date(trans.date).toLocaleDateString('en-US'),
              catName,
              subCatName,
              trans.description || '-',
              trans.type === 'income' ? 'Income' : 'Expense',
              `$${trans.amount.toFixed(2)}`
            ];
          }),
          theme: 'grid',
          styles: { fontSize: 7 },
          headStyles: { fillColor: [15, 23, 42] },
        });

        yPosition = doc.lastAutoTable.finalY + 10;
      }
    });

    if (productReport.length > 0) {
      if (yPosition > 220) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.text('Project/Product Report', 14, yPosition);
      yPosition += 10;

      autoTable(doc, {
        startY: yPosition,
        head: [['Project/Product', 'Income', 'Expenses', 'Balance']],
        body: productReport.map(item => [
          item.product_project,
          `$${item.income.toFixed(2)}`,
          `$${item.expense.toFixed(2)}`,
          `$${item.balance.toFixed(2)}`
        ]),
        theme: 'grid',
        styles: { fontSize: 10 },
        headStyles: { fillColor: [15, 23, 42] },
      });
    }

    doc.save(`EGO_CashFlow_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF report exported successfully');
  };

  const StatCard = ({ title, value, icon: Icon, type }) => (
    <div className="bg-card border border-border rounded-xl p-6 shadow-sm" data-testid={`report-stat-${type}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
          <p className={`text-3xl font-black font-mono mt-2 ${
            type === 'income' ? 'text-income' : type === 'expense' ? 'text-expense' : 'text-primary'
          }`}>
            ${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className={`p-3 rounded-lg ${
          type === 'income' ? 'bg-income/10' : type === 'expense' ? 'bg-expense/10' : 'bg-muted'
        }`}>
          <Icon size={24} className={type === 'income' ? 'text-income' : type === 'expense' ? 'text-expense' : 'text-primary'} strokeWidth={1.5} />
        </div>
      </div>
    </div>
  );

  const COLORS = ['#60A5FA', '#10B981', '#EF4444', '#F59E0B', '#94A3B8', '#8B5CF6', '#EC4899'];

  const parentCategories = categories.filter(c => !c.parent_id);
  const getSubcategories = (parentId) => categories.filter(c => c.parent_id === parentId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-3xl sm:text-4xl font-black font-heading tracking-tight text-primary">Detailed Reports</h2>
          <p className="text-sm sm:text-base text-muted-foreground mt-2">In-depth analysis of your financial data</p>
        </div>
        <Button onClick={exportToPDF} className="rounded-md w-full sm:w-auto" data-testid="export-pdf-button">
          <Download size={18} className="mr-2" />
          Export to PDF
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-secondary border border-border rounded-lg p-4">
        <h3 className="font-bold text-base sm:text-lg mb-4 font-heading text-primary">Report Filters</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <div>
            <Label htmlFor="report-start">Start Date</Label>
            <Input
              id="report-start"
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              data-testid="report-start-date"
            />
          </div>
          <div>
            <Label htmlFor="report-end">End Date</Label>
            <Input
              id="report-end"
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              data-testid="report-end-date"
            />
          </div>
          <div>
            <Label htmlFor="category-type">Category Type</Label>
            <Select value={filters.categoryType} onValueChange={(value) => setFilters({ ...filters, categoryType: value })}>
              <SelectTrigger id="category-type" data-testid="report-category-type">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="income">Income Only</SelectItem>
                <SelectItem value="expense">Expenses Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="category-filter">Category</Label>
            <Select 
              value={filters.categoryId} 
              onValueChange={(value) => setFilters({ ...filters, categoryId: value, subcategoryId: 'all' })}
            >
              <SelectTrigger id="category-filter" data-testid="report-category-filter">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {parentCategories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {filters.categoryId && filters.categoryId !== 'all' && getSubcategories(filters.categoryId).length > 0 && (
            <div>
              <Label htmlFor="subcategory-filter">Subcategory</Label>
              <Select 
                value={filters.subcategoryId} 
                onValueChange={(value) => setFilters({ ...filters, subcategoryId: value })}
              >
                <SelectTrigger id="subcategory-filter" data-testid="report-subcategory-filter">
                  <SelectValue placeholder="All subcategories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All subcategories</SelectItem>
                  {getSubcategories(filters.categoryId).map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label htmlFor="group-by">Date Grouping</Label>
            <Select value={filters.groupBy} onValueChange={(value) => setFilters({ ...filters, groupBy: value })}>
              <SelectTrigger id="group-by" data-testid="report-group-by">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Daily</SelectItem>
                <SelectItem value="month">Monthly</SelectItem>
                <SelectItem value="year">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard title="Total Income" value={summary.total_income} icon={TrendingUp} type="income" />
        <StatCard title="Total Expenses" value={summary.total_expense} icon={TrendingDown} type="expense" />
        <StatCard title="Net Balance" value={summary.balance} icon={Calendar} type="balance" />
        <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
          <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Transaction Count</p>
          <p className="text-3xl font-black font-mono mt-2 text-primary">{summary.transaction_count}</p>
        </div>
      </div>

      {/* Category Details Table */}
      <div className="bg-card border border-border rounded-xl p-4 sm:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
          <h3 className="text-lg sm:text-xl font-bold font-heading tracking-tight text-primary">Category Details</h3>
          {categoryReport.length > 0 && (
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={expandAll}
                className="text-xs px-2 py-1 h-7"
                data-testid="expand-all-btn"
              >
                Expand All
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={collapseAll}
                className="text-xs px-2 py-1 h-7"
                data-testid="collapse-all-btn"
              >
                Collapse All
              </Button>
            </div>
          )}
        </div>
        {categoryReport.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No data found</p>
        ) : (
          <div className="space-y-3">
            {categoryReport.map((categoryItem, catIndex) => {
              // Check if we're filtering by a subcategory
              const isSubcategoryFilter = filters.subcategoryId && filters.subcategoryId !== 'all';
              const isExpanded = expandedCategories[catIndex] || false;
              
              const categoryTransactions = allTransactions.filter(trans => {
                const catName = categories.find(c => c.id === trans.category_id)?.name;
                const subCatName = trans.subcategory_id ? categories.find(c => c.id === trans.subcategory_id)?.name : null;
                
                if (isSubcategoryFilter) {
                  // When subcategory filter is active, match by subcategory name
                  return subCatName === categoryItem.category_name;
                } else {
                  // When no subcategory filter, match by category name or subcategory name
                  return catName === categoryItem.category_name || subCatName === categoryItem.category_name;
                }
              });

              return (
                <div key={catIndex} className="border border-border rounded-lg overflow-hidden" data-testid={`category-detail-${catIndex}`}>
                  {/* Accordion Header - Clickable */}
                  <div 
                    className="bg-secondary p-3 cursor-pointer hover:bg-secondary/80 transition-colors"
                    onClick={() => toggleCategory(catIndex)}
                    data-testid={`category-header-${catIndex}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {isExpanded ? (
                          <ChevronUp size={18} className="text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronDown size={18} className="text-muted-foreground flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm sm:text-base font-bold text-primary truncate">{categoryItem.category_name}</h4>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                            <span>{categoryItem.transaction_count} txn</span>
                            <span>•</span>
                            <span>{categoryItem.percentage.toFixed(1)}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`text-base sm:text-lg font-black font-mono ${
                          categoryItem.total_amount >= 0 ? 'text-primary' : 'text-expense'
                        }`}>
                          ${categoryItem.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Accordion Content - Collapsible */}
                  {isExpanded && categoryTransactions.length > 0 && (
                    <div className="border-t border-border">
                      {/* Mobile Card View */}
                      <div className="sm:hidden divide-y divide-border">
                        {categoryTransactions
                          .sort((a, b) => new Date(b.date) - new Date(a.date))
                          .map((trans, transIndex) => {
                            const subCatName = trans.subcategory_id ? categories.find(c => c.id === trans.subcategory_id)?.name || '-' : '-';
                            return (
                              <div key={transIndex} className="p-3 bg-card" data-testid={`transaction-mobile-${catIndex}-${transIndex}`}>
                                <div className="flex justify-between items-start gap-2">
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs text-muted-foreground">
                                      {new Date(trans.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                      {subCatName !== '-' && ` • ${subCatName}`}
                                    </p>
                                    <p className="text-sm text-foreground truncate mt-0.5">
                                      {trans.description || 'No description'}
                                    </p>
                                  </div>
                                  <div className="text-right flex-shrink-0">
                                    <span className={`font-mono font-semibold text-sm ${
                                      trans.type === 'income' ? 'text-income' : 'text-expense'
                                    }`}>
                                      {trans.type === 'income' ? '+' : '-'}${trans.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                      
                      {/* Desktop Table View */}
                      <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-muted">
                            <tr>
                              <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground uppercase">Date</th>
                              <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground uppercase">Subcategory</th>
                              <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground uppercase">Description</th>
                              <th className="text-center py-2 px-2 text-xs font-medium text-muted-foreground uppercase">Type</th>
                              <th className="text-right py-2 px-3 text-xs font-medium text-muted-foreground uppercase">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {categoryTransactions
                              .sort((a, b) => new Date(b.date) - new Date(a.date))
                              .map((trans, transIndex) => {
                                const subCatName = trans.subcategory_id ? categories.find(c => c.id === trans.subcategory_id)?.name || '-' : '-';
                                return (
                                  <tr key={transIndex} className="border-b border-border hover:bg-muted/50" data-testid={`transaction-${catIndex}-${transIndex}`}>
                                    <td className="py-2 px-3 text-xs text-muted-foreground whitespace-nowrap">
                                      {new Date(trans.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </td>
                                    <td className="py-2 px-3 text-xs text-foreground">
                                      <div className="max-w-[120px] truncate">{subCatName}</div>
                                    </td>
                                    <td className="py-2 px-3 text-xs text-foreground">
                                      <div className="max-w-[200px] truncate">{trans.description || '-'}</div>
                                    </td>
                                    <td className="py-2 px-2 text-center">
                                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                        trans.type === 'income' ? 'bg-income/10 text-income' : 'bg-expense/10 text-expense'
                                      }`}>
                                        {trans.type === 'income' ? 'In' : 'Ex'}
                                      </span>
                                    </td>
                                    <td className={`py-2 px-3 text-right font-mono font-semibold text-xs whitespace-nowrap ${
                                      trans.type === 'income' ? 'text-income' : 'text-expense'
                                    }`}>
                                      {trans.type === 'income' ? '+' : '-'}${trans.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Product/Project Report */}
      {productReport.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
          <h3 className="text-xl font-bold font-heading tracking-tight text-primary mb-6">
            <Package className="inline mr-2" size={20} />
            Project/Product Report
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground uppercase tracking-wider">Project/Product</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground uppercase tracking-wider">Income</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground uppercase tracking-wider">Expenses</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground uppercase tracking-wider">Balance</th>
                </tr>
              </thead>
              <tbody>
                {productReport.map((item, index) => (
                  <tr key={index} className="border-b border-border table-row-hover" data-testid={`product-report-${index}`}>
                    <td className="py-3 px-4 text-sm font-medium text-foreground">{item.product_project}</td>
                    <td className="py-3 px-4 text-right font-mono font-semibold text-income">
                      ${item.income.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-4 text-right font-mono font-semibold text-expense">
                      ${item.expense.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className={`py-3 px-4 text-right font-mono font-bold ${
                      item.balance >= 0 ? 'text-income' : 'text-expense'
                    }`}>
                      ${item.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
          <h3 className="text-xl font-bold font-heading tracking-tight text-primary mb-6">Date-Based Trend</h3>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={dateReport}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="period" stroke="#94A3B8" style={{ fontSize: '12px' }} />
              <YAxis stroke="#94A3B8" style={{ fontSize: '12px' }} />
              <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155' }} />
              <Legend />
              <Line type="monotone" dataKey="income" stroke="#10B981" strokeWidth={2} name="Income" />
              <Line type="monotone" dataKey="expense" stroke="#EF4444" strokeWidth={2} name="Expenses" />
              <Line type="monotone" dataKey="balance" stroke="#60A5FA" strokeWidth={2} name="Balance" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
          <h3 className="text-xl font-bold font-heading tracking-tight text-primary mb-6">Category Distribution</h3>
          {categoryReport.length === 0 ? (
            <div className="flex items-center justify-center h-[350px] text-muted-foreground">No data found</div>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={categoryReport}
                  dataKey="total_amount"
                  nameKey="category_name"
                  cx="50%"
                  cy="50%"
                  outerRadius={110}
                  label={(entry) => `${entry.category_name} (${entry.percentage.toFixed(1)}%)`}
                >
                  {categoryReport.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
          <h3 className="text-xl font-bold font-heading tracking-tight text-primary mb-6">Category Comparison</h3>
          {categoryReport.length === 0 ? (
            <div className="flex items-center justify-center h-[350px] text-muted-foreground">No data found</div>
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={categoryReport}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="category_name" stroke="#94A3B8" style={{ fontSize: '11px' }} />
                <YAxis stroke="#94A3B8" style={{ fontSize: '12px' }} />
                <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155' }} />
                <Legend />
                <Bar dataKey="total_amount" fill="#60A5FA" name="Amount" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
          <h3 className="text-xl font-bold font-heading tracking-tight text-primary mb-6">Income vs Expenses</h3>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={dateReport}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="period" stroke="#94A3B8" style={{ fontSize: '12px' }} />
              <YAxis stroke="#94A3B8" style={{ fontSize: '12px' }} />
              <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155' }} />
              <Legend />
              <Bar dataKey="income" fill="#10B981" name="Income" />
              <Bar dataKey="expense" fill="#EF4444" name="Expenses" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
